import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, RefreshCw, Loader2, ListChecks, ArrowLeft } from 'lucide-react';
import { Pagination } from '../../components/client';
import { SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import { useAdminPlatformAudit } from '../../hooks/useAdminPlatformAudit';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const EVENT_TYPE_OPTIONS = [
 { value: '', label: 'All event types' },
 { value: 'audit.payout.recorded', label: 'Vendor payout recorded' },
 { value: 'audit.booking.date_change.approved', label: 'Booking date change approved' },
 { value: 'audit.booking.date_change.rejected', label: 'Booking date change rejected' },
 { value: 'audit.vendor.verification.approved', label: 'Vendor verification approved' },
 { value: 'audit.vendor.verification.rejected', label: 'Vendor verification rejected' },
];

const AdminComplianceAudit = () => {
 const [page, setPage] = useState(1);
 const [eventType, setEventType] = useState('');
 const { events, pagination, isLoading, error, refetch } = useAdminPlatformAudit({
 page,
 eventType,
 });

 const columns = [
 {
 key: 'created_at',
 label: 'When',
 width: 'w-40',
 render: (event) => (
 <div className="text-sm tabular-nums">
 <p className="text-ink">{event.created_at ? formatDate(event.created_at) : '—'}</p>
 <p className="text-gray-500 text-xs">
 {event.created_at ? formatTime(event.created_at) : ''}
 </p>
 </div>
 ),
 },
 {
 key: 'label',
 label: 'Event',
 render: (event) => (
 <div>
 <p className="text-sm font-medium text-ink">{event.label}</p>
 <p className="text-xs text-gray-500 font-mono">{event.event_type}</p>
 </div>
 ),
 },
 {
 key: 'actor',
 label: 'Actor',
 render: (event) => (
 <div className="min-w-0">
 <p className="text-sm text-ink truncate">{event.actor?.name || 'System'}</p>
 <p className="text-xs text-gray-500 truncate">{event.actor?.email || event.actor_type}</p>
 </div>
 ),
 },
 {
 key: 'aggregate',
 label: 'Subject',
 render: (event) =>
 event.aggregate_id ? (
 <span className="text-sm text-ink-secondary">
 {event.aggregate_type} ·{' '}
 <span className="font-mono text-xs">{String(event.aggregate_id).slice(0, 8)}…</span>
 </span>
 ) : (
 <span className="text-sm text-gray-400">—</span>
 ),
 },
 {
 key: 'ip_address',
 label: 'IP',
 width: 'w-28',
 render: (event) => (
 <span className="text-xs font-mono text-gray-500">{event.ip_address || '—'}</span>
 ),
 },
 ];

 if (error) {
 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Compliance audit log"
 subtitle="Platform compliance events from the outbox audit.events topic."
 />
 <div className="rounded-lg border border-red-200 bg-surface p-6 text-center shadow-sm">
 <p className="text-red-600">Failed to load compliance audit log.</p>
 </div>
 </div>
 );
 }

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title="Compliance audit log"
 subtitle="Platform compliance events from the outbox audit.events topic."
 actions={
 <div className="flex flex-wrap items-center gap-2">
 <Link
 to="/admin/analytics"
 className="inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 px-3.5 py-1.5 text-xs font-medium rounded-md border border-border bg-surface text-ink hover:bg-surface-muted"
 >
 <ArrowLeft className="w-4 h-4 shrink-0" />
 Back to Analytics
 </Link>
 <button
 type="button"
 onClick={() => refetch()}
 className="inline-flex items-center justify-center p-2.5 border border-border rounded-md bg-surface text-ink hover:bg-surface-muted"
 title="Refresh"
 >
 <RefreshCw className="w-4 h-4" />
 </button>
 </div>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.complianceAudit.title}
 steps={ADMIN_PAGE_GUIDES.complianceAudit.steps}
 icon={ListChecks}
 />

 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-5 space-y-4">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
 <p className="text-sm font-medium text-ink-secondary">Filter events</p>
 <div className="w-full sm:w-72 shrink-0">
 <SearchSelect
 value={eventType}
 onChange={(value) => {
 setEventType(value);
 setPage(1);
 }}
 options={EVENT_TYPE_OPTIONS}
 placeholder="All event types"
 />
 </div>
 </div>

 {isLoading ? (
 <div className="py-16 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading audit events…</p>
 </div>
 ) : events.length === 0 ? (
 <div className="text-center py-12 text-ink-tertiary">
 <FileCheck className="w-10 h-10 mx-auto mb-3 text-gray-300" />
 No compliance events found.
 </div>
 ) : (
 <>
 <DataTable
 columns={columns}
 data={events}
 searchable={false}
 filterable={false}
 showPagination={false}
 emptyMessage="No compliance events found"
 />
 {pagination.last_page > 1 && (
 <Pagination
 currentPage={pagination.current_page}
 lastPage={pagination.last_page}
 perPage={pagination.per_page}
 total={pagination.total}
 onPageChange={setPage}
 scrollToTop={false}
 />
 )}
 </>
 )}
 </div>
 </div>
 );
};

export default AdminComplianceAudit;
